import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button, DropdownList, FlexRow, SearchField, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {ConditionTile} from './ConditionTile';
import {isValueEditable, ReportTaxonomyField} from '../contracts/report-taxonomy.types';

const tokens = tokensForThemeProvider.sky.sys;

/** Grid of active filter-condition tiles plus an "Add condition" trigger that opens a searchable,
 * context-grouped picker of the remaining value-editable taxonomy fields. Renders nothing while
 * `taxonomy` hasn't loaded — `addCondition`/`updateConditionValues`/`removeCondition` all no-op
 * without it anyway (see ReportBuilderContext), so there is nothing useful to render. */
/** Filter fields the top-bar date control owns - all four of the fields any date scope can write
 * (the all-dates overlap pair, plus each narrow scope's own-date field) - so the conditions list
 * leaves them alone rather than rendering a second, un-editable copy of the date control. */
const PERIOD_FILTER_FIELDS = new Set(['START_DATE', 'END_DATE', 'OWN_START_DATE', 'OWN_DEADLINE']);

/** `DropdownList.Content` pads only vertically — `DropdownList.ContentGroup`/`.Item` supply their
 * own horizontal padding, but the search field and the empty-state text are bare content with
 * none, so without this they'd sit flush against the dropdown's edges instead of lining up with
 * the group headers/options below them. */
const InsetContent = styled.div`
	padding: 0 ${tokens.spacing['400']};
`;

export function ConditionsEditor(): JSX.Element | null {
	const {formatMessage} = useIntl();
	const {definition, taxonomy, addCondition, updateConditionValues, removeCondition} = useReportBuilder();
	const [search, setSearch] = useState('');

	const filterConditions = definition.filter.filterConditions;

	// The reporting period is edited by the date-range control in the top bar, which writes these two
	// conditions. Showing them here as well would present the same setting twice, in a tile that cannot
	// edit a date anyway. The original index is kept, because the update/remove callbacks address
	// conditions by their position in the full list.
	const editableConditions = useMemo(
		() =>
			filterConditions
				.map((condition, index) => ({condition, index}))
				.filter(({condition}) => !PERIOD_FILTER_FIELDS.has(condition.field)),
		[filterConditions]
	);

	// Tracks how many conditions share each field so tiles can be keyed by field identity (stable
	// across additions/removals elsewhere in the list) while still falling back to a unique key if
	// the same field ever appears twice (addCondition's own de-dupe against `usedFilterFields` below
	// prevents that via this control, but a condition list built elsewhere — e.g. an AI patch —
	// isn't guaranteed to be unique).
	const fieldOccurrences = useMemo(() => {
		const counts = new Map<string, number>();
		for (const condition of filterConditions) {
			counts.set(condition.field, (counts.get(condition.field) ?? 0) + 1);
		}
		return counts;
	}, [filterConditions]);

	// Conditions key on the filterField enum (STATUS, PROJECT, ...), not the taxonomy `key`, so the
	// add-picker must exclude a field whose `filterField` already has a condition (comparing against
	// `field.key` would never match and let duplicates through).
	const usedFilterFields = useMemo(() => new Set(filterConditions.map(condition => condition.field)), [filterConditions]);

	const fieldsByContext = useMemo(() => {
		const grouped = new Map<string, ReportTaxonomyField[]>();
		if (!taxonomy) {
			return grouped;
		}
		const lowerSearch = search.trim().toLowerCase();
		for (const field of taxonomy.fields) {
			// A field with no `filterField` cannot be expressed as a filter condition (it would store
			// `field: null`, which crashes downstream label/summary code). Such fields — e.g. the
			// derived booleans "Over estimate"/"Overdue" — must never be offered in the add picker,
			// even though they are otherwise value-editable (boolean).
			if (!field.filterField || !isValueEditable(taxonomy, field) || usedFilterFields.has(field.filterField)) {
				continue;
			}
			if (lowerSearch && !field.label.toLowerCase().includes(lowerSearch)) {
				continue;
			}
			const group = grouped.get(field.context) ?? [];
			group.push(field);
			grouped.set(field.context, group);
		}
		return grouped;
	}, [taxonomy, usedFilterFields, search]);

	if (!taxonomy) {
		return null;
	}

	const hasAvailableFields = [...fieldsByContext.values()].some(fields => fields.length > 0);

	return (
		<FlexRow gap={'xl'} wrap={'wrap'} alignItems={'flex-end'} data-cy={'ai-report-conditions-editor'}>
			{editableConditions.map(({condition, index}) => {
				const key = (fieldOccurrences.get(condition.field) ?? 0) > 1 ? `${condition.field}-${index}` : condition.field;
				return (
					<ConditionTile
						key={key}
						condition={condition}
						index={index}
						taxonomy={taxonomy}
						onChangeValues={updateConditionValues}
						onRemove={removeCondition}
					/>
				);
			})}
			<DropdownList>
				<DropdownList.Trigger>
					{/* Children must be a plain string, not a `<FormattedMessage>` element: `BaseButton`
					 * derives its `aria-label` from `children?.toString()` when non-waiting, and a React
					 * element stringifies to '[object Object]', breaking the accessible name (see
					 * AIBanner's/ColumnManager's Buttons, which use the same `formatMessage(...)` pattern
					 * for this reason). */}
					<Button emphasis={'low'} icon={'plus'} data-cy={'ai-report-condition-add'}>
						{formatMessage({id: 'ai_report_builder.conditions.add_condition'})}
					</Button>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					<InsetContent>
						<SearchField
							value={search}
							onChange={setSearch}
							isExpandable={false}
							width={'100%'}
							placeholder={formatMessage({id: 'common.search'})}
							data-cy={'ai-report-condition-add-search'}
						/>
					</InsetContent>
					{hasAvailableFields ? (
						taxonomy.contexts.map(context => {
							const fields = fieldsByContext.get(context.value);
							if (!fields || fields.length === 0) {
								return null;
							}
							return (
								<DropdownList.ContentGroup key={context.value} title={context.label}>
									{fields.map(field => (
										<DropdownList.Item
											key={field.key}
											onClick={() => addCondition(field.key)}
											data-cy={`ai-report-condition-add-option-${field.key}`}
										>
											{field.label}
										</DropdownList.Item>
									))}
								</DropdownList.ContentGroup>
							);
						})
					) : (
						// `fieldsByContext` has already applied `search`, so an empty list means one of two
						// different things. Claiming every field is in use while the user is looking at
						// their own non-matching search term states something that is simply untrue - the
						// fields are there, the term just does not match one.
						<InsetContent>
							<Text color={'medium'}>
								<FormattedMessage
									id={
										search.trim()
											? 'ai_report_builder.conditions.value_picker_empty'
											: 'ai_report_builder.conditions.add_empty'
									}
								/>
							</Text>
						</InsetContent>
					)}
				</DropdownList.Content>
			</DropdownList>
		</FlexRow>
	);
}
